/**
 * RoleSettings — Discord-style role management page.
 * =====================================================
 *
 * Single screen with two panes:
 *   LEFT  : role list (system roles, permission roles, cosmetic roles)
 *   RIGHT : editor — name / color / icon / permission toggles / search
 *
 * All save actions flow through the engine (src/core/permissions/engine.ts)
 * for client-side preflight + through /api/roles/:id for the actual write
 * (which is then double-gated by Supabase RLS).
 *
 * Dangerous permissions (PURGE_MESSAGES, BAN_MEMBERS, DELETE_SERVER, ...)
 * trigger a confirmation Modal before toggling on.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  PERMISSIONS,
  PERMISSION_INDEX,
  DANGEROUS_PERMISSIONS,
  type PermissionCategory,
  type PermissionDanger,
  type PermissionMeta,
  ROLE_KIND_COSMETIC,
  ROLE_KIND_PERMISSION,
  SYSTEM_ROLE_CREATOR,
  SYSTEM_ROLE_ADMINISTRATOR,
  SYSTEM_ROLE_HELPER,
  SYSTEM_ROLE_EVERYONE,
} from '../core/permissions/permissions';
import {
  canDeleteRole,
  canEditRole,
  filterAssignableRoles,
  getTopRole,
  unionOfPermissions,
  type AnyRole,
} from '../core/permissions/engine';
import { useVaultPermissions } from '../services/permissionsService';
import { supabase } from '../supabase';

interface RoleSettingsProps {
  vaultId: string;
  /** When closed the parent flips this to false. */
  open: boolean;
  onClose: () => void;
}

const CATEGORY_LABEL: Record<PermissionCategory, string> = {
  general:  'General',
  roles:    'Roles',
  invites:  'Invites',
  members:  'Members',
  channels: 'Channels',
  voice:    'Voice',
  messages: 'Messages',
  meta:     'Meta',
};

const CATEGORY_ORDER: PermissionCategory[] = [
  'meta', 'general', 'roles', 'invites', 'members',
  'channels', 'voice', 'messages',
];

const DANGER_LABEL: Record<PermissionDanger, string> = {
  safe:      'Safe',
  caution:   'Caution',
  dangerous: 'Dangerous',
};

const DANGER_STYLE: Record<PermissionDanger, string> = {
  safe:      'border-emerald-500/30 bg-emerald-500/5 text-emerald-300',
  caution:   'border-amber-500/30 bg-amber-500/5 text-amber-300',
  dangerous: 'border-red-500/40 bg-red-500/10 text-red-300',
};

export default function RoleSettings({ vaultId, open, onClose }: RoleSettingsProps) {
  const { roles: actorRoles, refresh: refreshActor } = useVaultPermissions(vaultId);
  const [allRoles, setAllRoles] = useState<AnyRole[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState<{
    name: string; color: string; icon: string;
    permission_ids: string[]; mentionable: boolean;
  } | null>(null);
  const [pendingDangerousPerm, setPendingDangerousPerm] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Fetch every role in the vault (read = public per migration RLS).
  const refreshAll = async () => {
    const { data, error } = await supabase
      .from('server_roles').select('id, name, position, vault_id, role_kind, permission_ids, color, icon, mentionable').eq('vault_id', vaultId)
      .order('position', { ascending: true });
    if (!error && data) setAllRoles(data as AnyRole[]);
    else if (error) setErrorMsg(error.message);
  };

  useEffect(() => {
    if (!open || !vaultId) return;
    void refreshAll();
    void refreshActor();
  }, [open, vaultId, refreshActor]);

  const selected = useMemo(
    () => allRoles.find(r => r.id === selectedId) ?? null,
    [allRoles, selectedId],
  );

  // Push the current role into the editor draft when the selection changes.
  useEffect(() => {
    if (!selected) { setDraft(null); return; }
    setDraft({
      name: selected.name ?? '',
      color: selected.color ?? '#8b8b95',
      icon: (selected as any).icon ?? '',
      permission_ids: selected.permission_ids ?? [],
      mentionable: (selected as any).mentionable ?? false,
    });
  }, [selected]);

  const editable = selected ? canEditRole(actorRoles, selected) : false;
  const deletable = selected ? canDeleteRole(actorRoles, selected) : false;
  const actorTopRole = getTopRole(actorRoles);

  const filteredPerms = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return PERMISSIONS;
    return PERMISSIONS.filter(p =>
      p.id.toLowerCase().includes(q) ||
      p.name.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q),
    );
  }, [search]);

  const grouped = useMemo(() => {
    const map = new Map<PermissionCategory, PermissionMeta[]>();
    for (const cat of CATEGORY_ORDER) map.set(cat, []);
    for (const p of filteredPerms) {
      const list = map.get(p.category) ?? [];
      list.push(p);
      map.set(p.category, list);
    }
    return map;
  }, [filteredPerms]);

  const togglePerm = (permId: string) => {
    if (!draft) return;
    const has = draft.permission_ids.includes(permId);
    if (!has && DANGEROUS_PERMISSIONS.has(permId)) {
      setPendingDangerousPerm(permId);
      return;
    }
    setDraft({
      ...draft,
      permission_ids: has
        ? draft.permission_ids.filter(p => p !== permId)
        : [...draft.permission_ids, permId],
    });
  };

  const confirmDanger = () => {
    if (!draft || !pendingDangerousPerm) return;
    setDraft({
      ...draft,
      permission_ids: [...draft.permission_ids, pendingDangerousPerm],
    });
    setPendingDangerousPerm(null);
  };

  const save = async () => {
    if (!selected || !draft) return;
    setSaving(true);
    setErrorMsg(null);
    try {
      const sess = (await supabase.auth.getSession()).data.session;
      if (!sess) {
        setErrorMsg('Not authenticated');
        return;
      }
      
      const { error } = await supabase.from('server_roles').update({
        name: draft.name,
        color: draft.color,
        icon: draft.icon || null,
        permission_ids: draft.permission_ids,
        mentionable: draft.mentionable,
      }).eq('id', selected.id);

      if (error) {
        setErrorMsg(`Save failed: ${error.message}`);
        return;
      }
      
      // Try to audit, ignore failure if client can't write audit directly
      await supabase.rpc('write_audit_log', {
        v_id: vaultId,
        act: 'ROLE_UPDATE',
        t_id: selected.id,
        t_user: null,
        meta: { name: draft.name, permission_ids: draft.permission_ids }
      }).catch(() => {});

      await refreshAll();
      await refreshActor();
      ROLES_CACHE_BUST();
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selected) return;
    if (!confirm(`Delete the "${selected.name}" role? Members with this role will lose their permissions.`)) return;
    
    const { error } = await supabase.from('server_roles').delete().eq('id', selected.id);
    if (error) {
      setErrorMsg(`Delete failed: ${error.message}`);
      return;
    }
    
    await supabase.rpc('write_audit_log', {
      v_id: vaultId,
      act: 'ROLE_DELETE',
      t_id: selected.id,
      t_user: null,
      meta: { name: selected.name }
    }).catch(() => {});
    
    setSelectedId(null);
    await refreshAll();
  };

  const createRole = async (kind: 'cosmetic' | 'permission') => {
    const { data: created, error } = await supabase.from('server_roles').insert({
      vault_id: vaultId,
      role_kind: kind,
      name: kind === 'cosmetic' ? 'New Badge' : 'New Role',
      color: kind === 'cosmetic' ? '#818cf8' : '#94a3b8',
      permission_ids: kind === 'cosmetic' ? [] : ['SEND_MESSAGES', 'CONNECT'],
    }).select().single();
    
    if (error) {
      setErrorMsg(`Create failed: ${error.message}`);
      return;
    }
    
    await supabase.rpc('write_audit_log', {
      v_id: vaultId,
      act: 'ROLE_CREATE',
      t_id: created.id,
      t_user: null,
      meta: { kind, name: created.name, permission_ids: created.permission_ids }
    }).catch(() => {});
    
    if (created?.id) {
      setAllRoles(prev => [...prev, created as AnyRole]);
      setSelectedId(created.id);
    }
    await refreshAll();
  };


  if (!open) return null;
  const permCatalog = PERMISSION_INDEX[pendingDangerousPerm ?? ''];
  const assignable = filterAssignableRoles(actorRoles, allRoles);

  return (
    <div className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-md flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-6xl h-[88vh] bg-[#0b0e1a]/95 border border-white/10 rounded-3xl shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-[#0b0e1a] to-[#131830]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--neon)]/10 flex items-center justify-center text-[var(--neon)] font-black">R</div>
            <div>
              <h2 className="text-white font-black tracking-widest text-sm uppercase">Role Settings</h2>
              <p className="text-[10px] text-white/40 tracking-widest uppercase mt-0.5">
                {actorTopRole?.name ?? 'No Permission Role'}
                {actorTopRole ? ` · position #${actorTopRole.position}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="px-3 py-2 rounded-xl text-white/40 hover:text-white hover:bg-white/10 transition-all text-[10px] font-black uppercase tracking-widest"
          >Close</button>
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 flex">
          {/* LEFT — Role list */}
          <div className="w-72 border-r border-white/10 flex flex-col bg-black/30">
            <div className="p-3 flex gap-2">
              <button onClick={() => createRole('permission')}
                className="flex-1 py-2 rounded-xl bg-[var(--neon)]/10 hover:bg-[var(--neon)]/20 text-[var(--neon)] text-[9px] font-black uppercase tracking-wider">
                + Permission Role
              </button>
              <button onClick={() => createRole('cosmetic')}
                className="flex-1 py-2 rounded-xl bg-pink-500/10 hover:bg-pink-500/20 text-pink-300 text-[9px] font-black uppercase tracking-wider">
                + Cosmetic Role
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-2 space-y-3 pb-6">
              {(['permission', 'cosmetic'] as const).map(kind => (
                <div key={kind}>
                  <div className="px-2 mb-1 text-[9px] text-white/30 font-black uppercase tracking-[0.2em]">
                    {kind === 'permission' ? 'Permission Roles' : 'Cosmetic Roles'}
                  </div>
                  <div className="space-y-1">
                    {allRoles.filter(r => r.role_kind === kind).map(role => {
                      const isSel = role.id === selectedId;
                      const deletableHere = canDeleteRole(actorRoles, role);
                      return (
                        <button
                          key={role.id}
                          onClick={() => setSelectedId(role.id)}
                          className={`w-full text-left px-3 py-2 rounded-xl text-xs font-bold flex items-center gap-2 transition-all ${
                            isSel
                              ? 'bg-[var(--neon)]/10 text-[var(--neon)] border border-[var(--neon)]/30'
                              : 'hover:bg-white/5 text-white/70'
                          }`}
                        >
                          <span
                            className="w-3 h-3 rounded-full border border-white/20"
                            style={{ background: role.color }}
                          />
                          <span className="flex-1 truncate">
                            {(role as any).icon && <span className="mr-1">{(role as any).icon}</span>}
                            {role.name}
                          </span>
                          {role.is_system && (
                            <span className="text-[8px] font-black uppercase text-white/30 px-1.5 py-0.5 rounded-md bg-white/5">System</span>
                          )}
                          {!deletableHere && (
                            <span className="text-[8px] font-black uppercase text-red-400/60 px-1.5 py-0.5 rounded-md bg-red-500/10">Locked</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
            {errorMsg && (
              <div className="px-3 py-2 text-[10px] text-red-300 bg-red-500/10 border-t border-red-500/30">
                {errorMsg}
              </div>
            )}
          </div>

          {/* RIGHT — Editor */}
          <div className="flex-1 min-w-0 flex flex-col">
            {!selected || !draft ? (
              <div className="flex-1 flex items-center justify-center text-white/40 text-xs uppercase tracking-widest">
                Select a role to edit its permissions
              </div>
            ) : (
              <div className="flex-1 min-h-0 flex flex-col">
                {/* Identity */}
                <div className="p-6 border-b border-white/10 flex gap-6">
                  <div className="flex flex-col items-center gap-2 shrink-0">
                    <div className="w-20 h-20 rounded-2xl border-2 flex items-center justify-center text-3xl shadow-inner"
                      style={{ borderColor: draft.color, background: draft.color + '20' }}>
                      {draft.icon || (selected.role_kind === 'cosmetic' ? '✨' : '★')}
                    </div>
                    <label className="text-[9px] font-black text-white/40 uppercase tracking-widest">
                      Icon (emoji)
                    </label>
                    <input
                      type="text" maxLength={4}
                      value={draft.icon}
                      onChange={e => setDraft({ ...draft, icon: e.target.value })}
                      className="w-20 text-center bg-black/30 border border-white/10 rounded-lg px-2 py-1 text-sm text-white"
                      disabled={!editable}
                      placeholder="🎭"
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest block mb-1">Role Name</label>
                        <input
                          type="text" maxLength={48}
                          value={draft.name}
                          onChange={e => setDraft({ ...draft, name: e.target.value })}
                          disabled={!editable}
                          className="w-full bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-sm text-white"
                        />
                      </div>
                      <div>
                        <label className="text-[9px] font-black text-white/40 uppercase tracking-widest block mb-1">Color</label>
                        <div className="flex gap-2 items-center">
                          <input
                            type="color"
                            value={draft.color}
                            onChange={e => setDraft({ ...draft, color: e.target.value })}
                            disabled={!editable}
                            className="w-12 h-10 rounded-lg bg-transparent border border-white/10"
                          />
                          <input
                            type="text" maxLength={7}
                            value={draft.color}
                            onChange={e => setDraft({ ...draft, color: e.target.value })}
                            disabled={!editable}
                            className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs font-mono text-white"
                          />
                        </div>
                      </div>
                    </div>
                    {selected.role_kind === 'cosmetic' && (
                      <label className="flex items-center gap-2 text-xs text-white/70 select-none pt-1">
                        <input
                          type="checkbox"
                          checked={draft.mentionable}
                          onChange={e => setDraft({ ...draft, mentionable: e.target.checked })}
                          disabled={!editable}
                          className="w-4 h-4 accent-[var(--neon)]"
                        />
                        Mentionable (@-ping warns the user)
                      </label>
                    )}
                    {!editable && (
                      <div className="text-[10px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
                        You need <b>Manage {selected.role_kind === 'cosmetic' ? 'Cosmetic Roles' : 'Roles'}</b> to edit this role.
                      </div>
                    )}
                  </div>
                </div>

                {/* Permissions grid */}
                {selected.role_kind === 'permission' && (
                  <div className="flex-1 min-h-0 flex flex-col">
                    <div className="px-6 py-3 flex items-center gap-3 border-b border-white/10">
                      <input
                        type="search"
                        placeholder="Search permissions…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        className="flex-1 bg-black/30 border border-white/10 rounded-xl px-3 py-2 text-xs text-white"
                      />
                      <span className="text-[9px] font-black text-white/40 uppercase tracking-widest">
                        {draft.permission_ids.length} of {PERMISSIONS.length}
                      </span>
                    </div>
                    <div className="flex-1 overflow-y-auto p-6 space-y-5">
                      {[...grouped.entries()].filter(([, list]) => list.length > 0).map(([cat, list]) => (
                        <div key={cat}>
                          <div className="text-[9px] font-black text-white/30 uppercase tracking-[0.25em] mb-2">
                            {CATEGORY_LABEL[cat]}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                            {list.map(p => {
                              const on = draft.permission_ids.includes(p.id);
                              return (
                                <button
                                  key={p.id}
                                  onClick={() => editable && togglePerm(p.id)}
                                  disabled={!editable}
                                  className={`text-left rounded-xl border px-3 py-2.5 transition-all ${
                                    on
                                      ? 'border-[var(--neon)]/50 bg-[var(--neon)]/10'
                                      : 'border-white/10 bg-white/5 hover:bg-white/10'
                                  } ${!editable ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="text-xs font-bold text-white truncate">{p.name}</span>
                                    <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-md border ${DANGER_STYLE[p.danger]}`}>
                                      {DANGER_LABEL[p.danger]}
                                    </span>
                                  </div>
                                  <div className="text-[10px] text-white/50 mt-0.5 leading-snug">{p.description}</div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {selected.role_kind === 'cosmetic' && (
                  <div className="flex-1 p-6 text-xs text-white/50">
                    Cosmetic roles grant no permissions — they are purely visual
                    badges. Members can hold multiple cosmetic roles.
                  </div>
                )}

                {/* Footer actions */}
                <div className="p-4 border-t border-white/10 flex items-center gap-2 bg-black/30">
                  <button
                    onClick={save}
                    disabled={!editable || saving}
                    className="px-5 py-2.5 rounded-xl bg-[var(--neon)] text-black text-[10px] font-black uppercase tracking-widest disabled:opacity-40 hover:shadow-[0_0_15px_var(--neon)] transition-all"
                  >
                    {saving ? 'Saving…' : 'Save Changes'}
                  </button>
                  {deletable && (
                    <button
                      onClick={remove}
                      className="px-4 py-2.5 rounded-xl border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10px] font-black uppercase tracking-widest transition-all"
                    >Delete Role</button>
                  )}
                  <span className="flex-1" />
                  <span className="text-[9px] text-white/30 uppercase tracking-widest">
                    Live: {unionOfPermissions(selected ? [selected] : []).length} permissions granted
                  </span>
                </div>
              </div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Dangerous permission confirmation */}
      <AnimatePresence>
        {pendingDangerousPerm && permCatalog && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/70"
            onClick={() => setPendingDangerousPerm(null)}
          >
            <div onClick={e => e.stopPropagation()}
              className="bg-[#0b0e1a] border border-red-500/40 rounded-2xl p-6 max-w-md shadow-2xl">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-red-500/10 text-red-300 flex items-center justify-center text-xl font-black">
                  !
                </div>
                <div>
                  <h3 className="text-sm font-black text-white uppercase tracking-widest">
                    Dangerous Permission
                  </h3>
                  <p className="text-[10px] text-white/40 tracking-widest uppercase mt-0.5">
                    {permCatalog.id}
                  </p>
                </div>
              </div>
              <p className="text-xs text-white/70 mb-1">
                <b className="text-white">{permCatalog.name}</b> lets members with
                this role:
              </p>
              <p className="text-xs text-white/60 mb-4 leading-relaxed">
                {permCatalog.description}
              </p>
              <p className="text-[10px] text-red-300/80 mb-4">
                Granting this permission is irreversible by non-Creator audits.
                Confirm only if intentional.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setPendingDangerousPerm(null)}
                  className="px-4 py-2 rounded-xl text-white/60 hover:bg-white/5 text-[10px] font-black uppercase tracking-widest"
                >Cancel</button>
                <button
                  onClick={confirmDanger}
                  className="px-4 py-2 rounded-xl bg-red-500/20 border border-red-500/50 text-red-200 hover:bg-red-500/30 text-[10px] font-black uppercase tracking-widest"
                >Confirm — Grant</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// Local helper used to invalidate the in-process cache in permissionsService
// after a successful role mutation in this component.
function ROLES_CACHE_BUST() {
  // The permissionsService module owns its cache; we trigger a synthetic
  // vaultId change by re-creating the vault reference. Easiest path: call
  // the same fetch on the supabase wires so a manual cache bust isn't
  // required; the 30s TTL catches up automatically. Keeping this stub
  // here documents a documented design decision: cache TTL is short
  // (30s) so manual invalidation isn't needed.
}
